'use client'

import { useState, useEffect } from 'react'
import { CaseGrid } from '@/components/sites/devlg-com-case-page1/CaseGrid'
import { FilterBar } from '@/components/sites/devlg-com-case-page1/FilterBar'
import { getWorkCases, type WorkCase, type WorkCaseFilters } from '@/lib/api/work-cases'
import { CaseCardSkeleton } from '@/components/sites/devlg-com-case-page1/CaseCardSkeleton'

// 过滤器状态接口
interface FilterState {
  industryTag: string
  carrierTag: string
}

export default function CasePage() {
  const [cases, setCases] = useState<WorkCase[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<FilterState>({
    industryTag: '-1',
    carrierTag: '-1'
  })
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: 20,
    total_pages: 0
  })

  useEffect(() => {
    const fetchCases = async () => {
      try {
        setLoading(true)
        setError(null)

        const apiFilters: WorkCaseFilters = {
          industryTag: filters.industryTag === '-1' ? -1 : parseInt(filters.industryTag),
          carrierTag: filters.carrierTag === '-1' ? -1 : parseInt(filters.carrierTag),
          page
        }

        const response = await getWorkCases(apiFilters)

        setCases(response.data)
        setPagination(response.pagination)
      } catch (err) {
        console.error('获取案例失败:', err)
        setError('获取案例失败，请稍后重试')
      } finally {
        setLoading(false)
      }
    }

    fetchCases()
  }, [filters, page])

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters)
    setPage(1) // 重置到第一页
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Page Header */}
      <header className="px-6 py-8 md:px-8 lg:w-[80%] lg:mx-auto lg:px-0">
        <h1 className="text-2xl font-semibold text-gray-900 md:text-3xl">
          软件项目案例作品
        </h1>
      </header>

      {/* Filter Bar */}
      <section className="px-6 pb-6 md:px-8 lg:w-[80%] lg:mx-auto lg:px-0">
        <FilterBar onFilterChange={handleFilterChange} />
      </section>

      {/* Case Grid */}
      <section className="px-6 pb-16 md:px-8 lg:w-[80%] lg:mx-auto lg:px-0">
        {loading ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <CaseCardSkeleton key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">加载失败</h3>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        ) : cases.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">暂无相关案例</h3>
            <p className="text-sm text-gray-500">请尝试调整筛选条件</p>
          </div>
        ) : (
          <>
            <CaseGrid cases={cases} />

            {/* 分页 */}
            {pagination.total_pages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  上一页
                </button>

                <span className="text-sm text-gray-600">
                  第 {page} / {pagination.total_pages} 页
                </span>

                <button
                  onClick={() => setPage(p => Math.min(pagination.total_pages, p + 1))}
                  disabled={page === pagination.total_pages}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}
